import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signUp, confirmSignUp, autoSignIn, resendSignUpCode, signInWithRedirect, fetchAuthSession } from 'aws-amplify/auth';
import { useAuth } from '../App';
import apiClient from '../api/client';
import { mapCognitoError, getPasswordStrength, isAtLeast13, MIN_SIGNUP_AGE, todayString } from '../utils/helpers';
import { trackEvent } from '../utils/analytics';
import useWindowWidth from '../hooks/useWindowWidth';
import Logo from '../components/Logo';

// Records DOB + age-confirmation timestamp + signup audit metadata. The
// backend also re-validates DOB and rejects under-13 (defense-in-depth).
// Idempotent server-side, fire-and-forget client-side — a failed audit must
// not block the user from reaching the dashboard. The age gate has already
// been enforced client-side before signUp() is called.
//
// Returns true if the record-signup POST returned OK; false otherwise.
// Used to fire the `signup_completed` analytics event only on a successful
// audit (the funnel definition pairs the event with the audit succeeding).
async function recordSignupAudit(ageConfirmedAt, dob) {
  if (!ageConfirmedAt || !dob) return false;
  try {
    await apiClient.post('/api/auth/record-signup', { ageConfirmedAt, dob });
    return true;
  } catch (e) {
    // Don't surface to user; logged in CloudWatch via the backend on retry/manual replay.
    console.warn('record-signup failed (non-blocking):', e?.message || e);
    return false;
  }
}

// Fire `signup_completed` once the record-signup POST returns OK. The signup
// audit is fire-and-forget for the navigation flow — we await it just for the
// analytics gate, then ignore the result for routing purposes.
async function fireSignupCompleted(ageConfirmedAt, dob, gradYear, method) {
  const ok = await recordSignupAudit(ageConfirmedAt, dob);
  if (ok) {
    trackEvent('signup_completed', {
      method,
      grad_year: gradYear ? Number(gradYear) : undefined,
    });
  }
}

// Friction layer to make refresh-to-retry useless after an under-13 rejection.
// Trivially bypassable in incognito — that's fine, this isn't the real gate.
const AGE_DENIED_KEY = 'bragsheet_age_denied';
const AGE_DENIED_TTL_MS = 24 * 60 * 60 * 1000;

function readAgeDeniedFlag() {
  try {
    const raw = window.localStorage.getItem(AGE_DENIED_KEY);
    if (!raw) return false;
    // Stored as either "true" + a sibling timestamp, or as JSON { at: <ms> }
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.at !== 'number') return false;
    return Date.now() - parsed.at < AGE_DENIED_TTL_MS;
  } catch (_) {
    return false;
  }
}

function writeAgeDeniedFlag() {
  try {
    window.localStorage.setItem(AGE_DENIED_KEY, JSON.stringify({ value: true, at: Date.now() }));
  } catch (_) {
    // Storage may be disabled (private mode on some browsers); fail silently.
  }
}

// T18: Split-panel auth layout (mirrors LoginPage).
const styles = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#f8fafc',
    display: 'flex',
    alignItems: 'stretch',
    justifyContent: 'center',
  },
  splitContainer: (isMobile) => ({
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row',
    width: '100%',
    minHeight: '100vh',
  }),
  brandPanel: {
    flex: '0 0 40%',
    backgroundColor: '#01696F',
    color: '#ffffff',
    padding: '3rem 2.5rem',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    position: 'relative',
    overflow: 'hidden',
  },
  brandLogoWrap: {
    marginBottom: '2rem',
    filter: 'brightness(0) invert(1)',
  },
  brandHeadline: {
    fontSize: '2rem',
    fontWeight: '700',
    lineHeight: 1.2,
    marginBottom: '1rem',
    color: '#ffffff',
  },
  brandTagline: {
    fontSize: '1rem',
    fontWeight: '400',
    lineHeight: 1.5,
    color: 'rgba(255,255,255,0.85)',
    maxWidth: '380px',
  },
  formPanel: {
    flex: '1 1 60%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2rem',
    backgroundColor: '#f8fafc',
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    border: '1px solid #e2e8f0',
    padding: '2.5rem',
    width: '100%',
    maxWidth: '440px',
    boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
  },
  logo: {
    fontSize: '1.75rem',
    fontWeight: '700',
    color: '#01696F',
    textAlign: 'center',
    marginBottom: '0.5rem',
  },
  subtitle: {
    textAlign: 'center',
    color: '#64748b',
    fontSize: '0.9rem',
    marginBottom: '2rem',
  },
  formGroup: {
    marginBottom: '1.1rem',
  },
  label: {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: '500',
    color: '#1e293b',
    marginBottom: '0.4rem',
  },
  input: {
    width: '100%',
    padding: '0.75rem 0.875rem',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    fontSize: '0.9rem',
    color: '#1e293b',
    outline: 'none',
  },
  select: {
    width: '100%',
    padding: '0.75rem 0.875rem',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    fontSize: '0.9rem',
    color: '#1e293b',
    backgroundColor: '#ffffff',
    outline: 'none',
  },
  btn: {
    width: '100%',
    padding: '0.75rem',
    backgroundColor: '#01696F',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '0.95rem',
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: '0.5rem',
    minHeight: '44px',
  },
  btnDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  error: {
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    color: '#ef4444',
    borderRadius: '8px',
    padding: '0.75rem',
    fontSize: '0.875rem',
    marginBottom: '1rem',
  },
  info: {
    backgroundColor: '#f0fdfa',
    border: '1px solid #99f6e4',
    color: '#01696F',
    borderRadius: '8px',
    padding: '0.75rem',
    fontSize: '0.875rem',
    marginBottom: '1rem',
  },
  footer: {
    textAlign: 'center',
    marginTop: '1.5rem',
    fontSize: '0.875rem',
    color: '#64748b',
  },
  footerLink: {
    color: '#01696F',
    fontWeight: '500',
    textDecoration: 'none',
  },
  resendBtn: {
    width: '100%',
    padding: '0.6rem',
    backgroundColor: 'transparent',
    color: '#01696F',
    border: '1px solid #01696F',
    borderRadius: '8px',
    fontSize: '0.875rem',
    fontWeight: '500',
    cursor: 'pointer',
    marginTop: '0.75rem',
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    margin: '1.5rem 0',
    color: '#94a3b8',
    fontSize: '0.8rem',
  },
  dividerLine: {
    flex: 1,
    height: '1px',
    backgroundColor: '#e2e8f0',
  },
  googleBtn: {
    width: '100%',
    padding: '0.75rem',
    backgroundColor: '#ffffff',
    color: '#1e293b',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '0.9rem',
    fontWeight: '500',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    minHeight: '44px',
    transition: 'border-color 0.15s, box-shadow 0.15s',
  },
  googleIcon: {
    width: '18px',
    height: '18px',
  },
  passwordWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  toggleBtn: {
    position: 'absolute',
    right: '0',
    top: '0',
    bottom: '0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '44px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '0',
    color: '#94a3b8',
  },
};

const EyeIcon = ({ open }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {open ? (
      <>
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
      </>
    ) : (
      <>
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
        <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
        <line x1="1" y1="1" x2="23" y2="23" />
      </>
    )}
  </svg>
);

export default function RegisterPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [gradYear, setGradYear] = useState('2026');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const [confirmStep, setConfirmStep] = useState(false);
  const [confirmCode, setConfirmCode] = useState('');
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [googleError, setGoogleError] = useState('');
  const [dob, setDob] = useState('');
  const [ageConfirmedAt, setAgeConfirmedAt] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  // Locked-out state: a previous attempt was rejected for being under 13.
  // Persists for AGE_DENIED_TTL_MS so a refresh doesn't reset the form.
  const [ageDenied, setAgeDenied] = useState(() => readAgeDeniedFlag());
  const isGoogleConfigured = Boolean(process.env.REACT_APP_COGNITO_DOMAIN);
  const passwordStrength = getPasswordStrength(password);

  const handleGoogleSignIn = async () => {
    setError('');
    setGoogleError('');
    if (!isGoogleConfigured) {
      setGoogleError('Google sign-in is not configured yet. Please use email and password.');
      return;
    }
    try {
      await signInWithRedirect({ provider: 'Google' });
    } catch (err) {
      setGoogleError(err.message || 'Google sign-in failed. Please try again.');
    }
  };

  const intervalRef = useRef(null);
  const { login } = useAuth();
  const navigate = useNavigate();
  const { isMobile } = useWindowWidth();

  // Amplify v6's confirmSignUp can establish a Cognito session as a side effect.
  // When that happens, our subsequent autoSignIn() call may throw or return no
  // tokens, but a valid session does exist. This helper checks for it and adopts
  // it (login + navigate to dashboard). Returns true if a session was adopted,
  // false if the user is genuinely unauthenticated.
  const adoptCognitoSession = async (overrideConfirmedAt) => {
    try {
      const session = await fetchAuthSession();
      const idToken = session?.tokens?.idToken?.toString();
      if (!idToken) return false;
      const payload = session.tokens.idToken.payload || {};
      login(idToken, {
        id: payload.sub,
        email: payload.email,
        name: payload.name || payload['cognito:username'] || name,
      });
      fireSignupCompleted(overrideConfirmedAt || ageConfirmedAt, dob, gradYear, 'email');
      navigate('/dashboard');
      return true;
    } catch (_) {
      return false;
    }
  };

  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  const handleResend = async () => {
    setError('');
    setInfo('');
    setResendCooldown(30);
    intervalRef.current = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) {
          clearInterval(intervalRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    try {
      await resendSignUpCode({ username: email });
      setInfo('A new code has been sent to your email.');
    } catch (err) {
      setError(mapCognitoError(err));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');
    // Age gate — required client-side check. Backend re-validates DOB in
    // /api/auth/record-signup as defense-in-depth, but rejecting here avoids
    // creating a Cognito user we'd otherwise have to clean up.
    if (!dob) {
      setError('Please enter your date of birth.');
      return;
    }
    if (!isAtLeast13(dob)) {
      setError(`BragSheet is only available to users ${MIN_SIGNUP_AGE} and older.`);
      writeAgeDeniedFlag();
      setAgeDenied(true);
      return;
    }
    // Stamp the moment the user passed the age gate. Set both state (so
    // post-confirm-email flows can still read it via the closure rebuilt on
    // re-render) and a local variable used for this handler's record-signup
    // call (avoids stale-closure issues within this same submit).
    const confirmedAtNow = ageConfirmedAt || new Date().toISOString();
    if (!ageConfirmedAt) setAgeConfirmedAt(confirmedAtNow);
    setLoading(true);
    try {
      const { nextStep } = await signUp({
        username: email,
        password,
        options: {
          userAttributes: {
            name,
            'custom:gradYear': String(gradYear),
          },
          autoSignIn: true,
        },
      });

      if (nextStep.signUpStep === 'CONFIRM_SIGN_UP') {
        // Email verification required — show confirmation code input
        setConfirmStep(true);
        setInfo('Check your email for a verification code and enter it below.');
        return;
      }

      // autoSignIn resolves when email verification is not required.
      // If it throws or returns no token, fall back to checking for an
      // existing Cognito session (set as a side effect by signUp).
      let signInResult;
      try {
        signInResult = await autoSignIn();
      } catch (_) {
        const adopted = await adoptCognitoSession(confirmedAtNow);
        if (!adopted) navigate('/login');
        return;
      }
      const idToken = signInResult.tokens && signInResult.tokens.idToken
        ? signInResult.tokens.idToken.toString()
        : null;
      if (!idToken) {
        const adopted = await adoptCognitoSession(confirmedAtNow);
        if (!adopted) navigate('/login');
        return;
      }
      const payload = signInResult.tokens && signInResult.tokens.idToken
        ? signInResult.tokens.idToken.payload
        : {};
      const userPayload = {
        id: payload.sub,
        email: payload.email,
        name: payload.name || name,
      };
      login(idToken, userPayload);
      fireSignupCompleted(confirmedAtNow, dob, gradYear, 'email');
      navigate('/dashboard');
    } catch (err) {
      setError(mapCognitoError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setConfirmLoading(true);
    try {
      const { nextStep } = await confirmSignUp({ username: email, confirmationCode: confirmCode });
      // Cognito confirmSignUp succeeded → user verified their email. Fire
      // before the autoSignIn branching so we capture the event even when
      // session adoption is what completes the login.
      trackEvent('email_verified', { method: 'email' });

      if (nextStep.signUpStep === 'COMPLETE_AUTO_SIGN_IN' || nextStep.signUpStep === 'DONE') {
        // Attempt autoSignIn after successful confirmation
        try {
          const signInResult = await autoSignIn();
          const idToken = signInResult.tokens && signInResult.tokens.idToken
            ? signInResult.tokens.idToken.toString()
            : null;
          if (!idToken) {
            // autoSignIn returned no token — but Cognito may already have a
            // session from confirmSignUp's side effect. Try to adopt it.
            const adopted = await adoptCognitoSession();
            if (!adopted) navigate('/login');
            return;
          }
          const payload = signInResult.tokens && signInResult.tokens.idToken
            ? signInResult.tokens.idToken.payload
            : {};
          const userPayload = {
            id: payload.sub,
            email: payload.email,
            name: payload.name || name,
          };
          login(idToken, userPayload);
          fireSignupCompleted(ageConfirmedAt, dob, gradYear, 'email');
          navigate('/dashboard');
        } catch (_) {
          // autoSignIn failed — but Cognito may already have a session
          // from confirmSignUp's side effect. Try to adopt it before
          // bouncing the user to /login.
          const adopted = await adoptCognitoSession();
          if (!adopted) navigate('/login');
        }
      } else {
        navigate('/login');
      }
    } catch (err) {
      setError(mapCognitoError(err));
    } finally {
      setConfirmLoading(false);
    }
  };

  // T18: Brand panel rendered as a sibling on desktop, hidden on mobile.
  // Defined as a render helper so each return path (confirmStep, ageDenied,
  // main) can reuse the same layout without copy-pasting markup.
  const BrandPanel = () => (
    !isMobile ? (
      <div style={styles.brandPanel}>
        <div style={styles.brandLogoWrap}>
          <Link to="/" style={{ textDecoration: 'none' }}><Logo size="large" /></Link>
        </div>
        <h1 style={styles.brandHeadline}>Your college journey starts here.</h1>
        <p style={styles.brandTagline}>
          Capture wins, build your story, and walk into application season ready.
        </p>
      </div>
    ) : null
  );

  if (confirmStep) {
    return (
      <div style={styles.page}>
        <div style={styles.splitContainer(isMobile)}>
          <BrandPanel />
          <div style={styles.formPanel}>
            <div style={{ ...styles.card, padding: isMobile ? '1.5rem 1.25rem' : '2.5rem' }}>
          <div style={{ textAlign: 'center', marginBottom: '0.5rem' }}><Link to="/" style={{ textDecoration: 'none' }}><Logo size="medium" /></Link></div>
          <div style={styles.subtitle}>Verify your email</div>

          {info && <div style={styles.info}>{info}</div>}
          {error && <div style={styles.error}>{error}</div>}

          <form onSubmit={handleConfirm}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Verification Code</label>
              <input
                style={styles.input}
                type="text"
                value={confirmCode}
                onChange={e => setConfirmCode(e.target.value)}
                placeholder="Enter 6-digit code"
                required
                autoFocus
                onFocus={(e) => { e.target.style.borderColor = '#01696F'; e.target.style.boxShadow = '0 0 0 3px rgba(1, 105, 111, 0.15)'; }}
                onBlur={(e) => { e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none'; }}
              />
            </div>
            <button
              type="submit"
              style={{ ...styles.btn, ...(confirmLoading ? styles.btnDisabled : {}) }}
              disabled={confirmLoading}
            >
              {confirmLoading ? 'Verifying...' : 'Verify Email'}
            </button>
          </form>

          <button
            type="button"
            style={{ ...styles.resendBtn, ...(resendCooldown > 0 ? styles.btnDisabled : {}) }}
            onClick={handleResend}
            disabled={resendCooldown > 0}
          >
            {resendCooldown > 0 ? `Resend in ${resendCooldown}s...` : 'Resend code'}
          </button>

          <div style={styles.footer}>
            Already verified?{' '}
            <Link to="/login" style={styles.footerLink}>Sign in</Link>
          </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Hard-block render path: a previous attempt was rejected as under-13.
  // Persists for 24h via localStorage. Trivially bypassable in incognito —
  // this is friction, not enforcement. The real gate is the server check in
  // /api/auth/record-signup.
  if (ageDenied) {
    return (
      <div style={styles.page}>
        <div style={styles.splitContainer(isMobile)}>
          <BrandPanel />
          <div style={styles.formPanel}>
            <div style={{ ...styles.card, padding: isMobile ? '1.5rem 1.25rem' : '2.5rem' }}>
              <div style={{ textAlign: 'center', marginBottom: '0.5rem' }}><Link to="/" style={{ textDecoration: 'none' }}><Logo size="medium" /></Link></div>
              <div style={styles.subtitle}>Account creation unavailable</div>
              <div style={styles.error}>
                BragSheet is only available to users {MIN_SIGNUP_AGE} and older.
              </div>
              <p style={{ fontSize: '0.85rem', color: '#64748b', lineHeight: 1.5, margin: '0 0 1rem' }}>
                We can't create an account for you at this time. If this was a mistake,
                please come back when you're {MIN_SIGNUP_AGE} or older.
              </p>
              <div style={styles.footer}>
                <Link to="/" style={styles.footerLink}>Back to home</Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.splitContainer(isMobile)}>
        <BrandPanel />
        <div style={styles.formPanel}>
          <div style={{ ...styles.card, padding: isMobile ? '1.5rem 1.25rem' : '2.5rem' }}>
            <div style={{ textAlign: 'center', marginBottom: '0.5rem' }}><Link to="/" style={{ textDecoration: 'none' }}><Logo size="medium" /></Link></div>
            <div style={styles.subtitle}>Start your college journey today</div>

        {error && <div style={styles.error}>{error}</div>}

        <form onSubmit={handleSubmit}>
          <div
            role="note"
            style={{
              background: '#f0f9f9',
              border: '1px solid #b8e0e0',
              borderRadius: '8px',
              padding: '0.75rem 1rem',
              marginBottom: '1rem',
              fontSize: '0.85rem',
              color: '#01696F',
            }}
          >
            BragSheet is only available to users {MIN_SIGNUP_AGE} and older. We collect your date of birth to verify your eligibility.
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Full Name</label>
            <input
              style={styles.input}
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Alex Johnson"
              required
              autoFocus
              onFocus={(e) => { e.target.style.borderColor = '#01696F'; e.target.style.boxShadow = '0 0 0 3px rgba(1, 105, 111, 0.15)'; }}
              onBlur={(e) => { e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none'; }}
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Email</label>
            <input
              style={styles.input}
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              onFocus={(e) => { e.target.style.borderColor = '#01696F'; e.target.style.boxShadow = '0 0 0 3px rgba(1, 105, 111, 0.15)'; }}
              onBlur={(e) => { e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none'; }}
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Password</label>
            <div style={styles.passwordWrapper}>
              <input
                style={{ ...styles.input, paddingRight: '2.75rem' }}
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Create a strong password (min 8 characters)"
                required
                minLength={8}
                onFocus={(e) => { e.target.style.borderColor = '#01696F'; e.target.style.boxShadow = '0 0 0 3px rgba(1, 105, 111, 0.15)'; }}
                onBlur={(e) => { e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none'; }}
              />
              <button
                type="button"
                style={styles.toggleBtn}
                onClick={() => setShowPassword(v => !v)}
                tabIndex={-1}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                <EyeIcon open={showPassword} />
              </button>
            </div>
            {password.length > 0 && (
              <div style={{ marginTop: '0.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                  <div style={{
                    flex: 1, height: '4px', backgroundColor: '#e2e8f0', borderRadius: '2px', overflow: 'hidden',
                  }}>
                    <div style={{
                      height: '100%', borderRadius: '2px',
                      width: `${(passwordStrength.score / 6) * 100}%`,
                      backgroundColor: { weak: '#ef4444', fair: '#f97316', good: '#84cc16', strong: '#16a34a' }[passwordStrength.level],
                      transition: 'width 0.2s, background-color 0.2s',
                    }} />
                  </div>
                  <span style={{
                    fontSize: '0.75rem', fontWeight: '600', minWidth: '45px',
                    color: { weak: '#ef4444', fair: '#f97316', good: '#84cc16', strong: '#16a34a' }[passwordStrength.level],
                  }}>{passwordStrength.label}</span>
                </div>
                {passwordStrength.suggestions.length > 0
                  && passwordStrength.label !== 'Strong'
                  && passwordStrength.label !== 'Very Strong' && (
                  <p style={{ fontSize: '0.75rem', color: '#94a3b8', margin: 0 }}>
                    {passwordStrength.suggestions[0]}
                  </p>
                )}
              </div>
            )}
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Graduation Year</label>
            <select
              style={styles.select}
              value={gradYear}
              onChange={e => setGradYear(e.target.value)}
              required
            >
              <option value="2025">2025</option>
              <option value="2026">2026</option>
              <option value="2027">2027</option>
              <option value="2028">2028</option>
              <option value="2029">2029</option>
            </select>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Date of Birth</label>
            <input
              style={styles.input}
              type="date"
              value={dob}
              onChange={e => setDob(e.target.value)}
              max={todayString()}
              required
              aria-describedby="dob-help"
              onFocus={(e) => { e.target.style.borderColor = '#01696F'; e.target.style.boxShadow = '0 0 0 3px rgba(1, 105, 111, 0.15)'; }}
              onBlur={(e) => { e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none'; }}
            />
            <p id="dob-help" style={{ fontSize: '0.75rem', color: '#94a3b8', margin: '0.4rem 0 0', lineHeight: 1.4 }}>
              BragSheet is only available to users {MIN_SIGNUP_AGE} and older.
            </p>
          </div>
          <div style={{ marginBottom: '0.75rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.8rem', color: '#64748b', minHeight: '44px' }}>
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={e => setTermsAccepted(e.target.checked)}
                style={{ width: '20px', height: '20px', flexShrink: 0, accentColor: '#01696F', cursor: 'pointer' }}
              />
              <span>I agree to the{' '}
                <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: '#01696F', textDecoration: 'underline' }}>Terms of Service</a>
                {' '}and{' '}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#01696F', textDecoration: 'underline' }}>Privacy Policy</a>
              </span>
            </label>
          </div>
          <button
            type="submit"
            style={{ ...styles.btn, ...(loading || !dob || !termsAccepted ? styles.btnDisabled : {}) }}
            disabled={loading || !dob || !termsAccepted}
          >
            {loading ? 'Creating account...' : 'Create Account'}
          </button>
        </form>

        <div style={styles.divider}>
          <span style={styles.dividerLine} />
          <span>or</span>
          <span style={styles.dividerLine} />
        </div>

        {googleError && <div style={styles.error}>{googleError}</div>}

        <button
          type="button"
          style={styles.googleBtn}
          onClick={handleGoogleSignIn}
          onMouseEnter={(e) => { e.target.style.borderColor = '#01696F'; e.target.style.boxShadow = '0 0 0 3px rgba(1, 105, 111, 0.1)'; }}
          onMouseLeave={(e) => { e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none'; }}
        >
          <svg style={styles.googleIcon} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
          Sign up with Google
        </button>

        <div style={styles.footer}>
          Already have an account?{' '}
          <Link to="/login" style={styles.footerLink}>Sign in</Link>
        </div>
          </div>
        </div>
      </div>
    </div>
  );
}
